import { useCallback, useEffect, useRef, useState } from 'react';
import { api, simulate } from './api';
import { usePrefs } from './prefs';

export type LiveState<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; error: Error & { code?: string; requestId?: string } };

/* Carrega dados próprios do usuário (source "live"). Respeita os modos "carregando" e "erro"
   do Laboratório de estados; "set" atualiza a tela após uma alteração sem recarregar tudo. */
export function useLive<B, T>(path: string, pick: (body: B) => T) {
  const { stateMode } = usePrefs();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<LiveState<T>>({ status: 'loading' });
  const pickRef = useRef(pick);
  pickRef.current = pick;

  useEffect(() => {
    const ac = new AbortController();
    setState({ status: 'loading' });
    simulate(stateMode, ac.signal)
      .then(() => api.get<B>(path, ac.signal))
      .then(
        (body) => { if (!ac.signal.aborted) setState({ status: 'ready', data: pickRef.current(body) }); },
        (error: Error) => { if (!ac.signal.aborted) setState({ status: 'error', error }); }
      );
    return () => ac.abort();
  }, [path, stateMode, attempt]);

  const set = useCallback((update: (prev: T) => T) => {
    setState((s) => (s.status === 'ready' ? { status: 'ready', data: update(s.data) } : s));
  }, []);

  return { state, set, reload: () => setAttempt((a) => a + 1) };
}
